import { Node, mergeAttributes } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { closeHistory } from '@tiptap/pm/history';

export const ColumnsNode = Node.create({
    name: 'layoutColumns', group: 'block', content: 'layoutColumn{2,3}', defining: true, isolating: true,
    parseHTML() { return [{ tag: 'div[data-type="layout-columns"]' }]; },
    renderHTML({ node, HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'layout-columns', class: 'lesson-columns', 'data-columns': node.childCount }), 0];
    }
});
export const ColumnNode = Node.create({
    name: 'layoutColumn', content: 'block+', defining: true, isolating: true,
    parseHTML() { return [{ tag: 'div[data-type="layout-column"]' }]; },
    renderHTML({ HTMLAttributes }) { return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'layout-column', class: 'lesson-column' }), 0]; }
});

export function columnsContext(editor: Editor) {
    const { $from, from } = editor.state.selection;
    const selected = editor.state.doc.nodeAt(from);
    if (selected?.type.name === 'layoutColumns') return { group: selected, pos: from, index: 0 };
    for (let depth = $from.depth; depth > 0; depth--) {
        if ($from.node(depth).type.name === 'layoutColumns') return { group: $from.node(depth), pos: $from.before(depth), index: $from.index(depth) };
    }
    return null;
}

/** Resizing keeps all content: an omitted column is appended to the last one. */
export function setColumns(editor: Editor, count: 2 | 3) {
    if (count !== 2 && count !== 3) return false;
    const context = columnsContext(editor);
    const schema = editor.schema;
    if (!context) {
        return editor.chain().focus().command(({ state, tr, dispatch }) => {
            const range = state.selection.$from.blockRange(state.selection.$to);
            if (!range || !range.parent.canReplaceWith(range.startIndex, range.endIndex, schema.nodes.layoutColumns)) return false;
            const content = state.doc.slice(range.start, range.end).content;
            const columns = [schema.nodes.layoutColumn.create(null, content)];
            while (columns.length < count) columns.push(schema.nodes.layoutColumn.create(null, schema.nodes.paragraph.create()));
            if (dispatch) {
                tr.replaceWith(range.start, range.end, schema.nodes.layoutColumns.create(null, columns));
                tr.setSelection(TextSelection.near(tr.doc.resolve(range.start + 2))).scrollIntoView();
                closeHistory(tr);
            }
            return true;
        }).run();
    }
    if (context.group.childCount === count) return true;
    const columns = Array.from({ length: context.group.childCount }, (_, i) => context.group.child(i));
    while (columns.length > count) {
        const removed = columns.pop()!;
        columns[columns.length - 1] = columns[columns.length - 1].copy(columns[columns.length - 1].content.append(removed.content));
    }
    while (columns.length < count) columns.push(schema.nodes.layoutColumn.create(null, schema.nodes.paragraph.create()));
    const tr = editor.state.tr.replaceWith(context.pos, context.pos + context.group.nodeSize, context.group.copy(Fragment.fromArray(columns)));
    editor.view.dispatch(closeHistory(tr).setSelection(TextSelection.near(tr.doc.resolve(context.pos + 2))).scrollIntoView());
    editor.commands.focus();
    return true;
}

export function changeColumn(editor: Editor, action: 'previous' | 'next' | 'unwrap') {
    const context = columnsContext(editor);
    if (!context) return false;
    const columns = Array.from({ length: context.group.childCount }, (_, i) => context.group.child(i));
    let content: Fragment;
    let selection = context.pos + 1;
    if (action === 'unwrap') {
        content = Fragment.fromArray(columns.flatMap(column => Array.from({ length: column.childCount }, (_, i) => column.child(i))));
    } else {
        const target = context.index + (action === 'previous' ? -1 : 1);
        if (target < 0 || target >= columns.length) return false;
        [columns[context.index], columns[target]] = [columns[target], columns[context.index]];
        content = Fragment.from(context.group.copy(Fragment.fromArray(columns)));
        selection = context.pos + 2 + columns.slice(0, target).reduce((sum, column) => sum + column.nodeSize, 0);
    }
    const tr = editor.state.tr.replaceWith(context.pos, context.pos + context.group.nodeSize, content);
    editor.view.dispatch(closeHistory(tr).setSelection(TextSelection.near(tr.doc.resolve(selection))).scrollIntoView());
    editor.commands.focus();
    return true;
}
